import React, { useEffect, useState } from 'react';
import { useAuth } from '@soap/modules';
import { getPrimaryFunctionAppRoot } from './functionAppRoot';
import { isDirectHttpImageUrl, parseBlobIdFromImageUrl } from './blobImageUrl';

export function useAuthenticatedImageSrc(
    imageUrl: string | null | undefined,
    blobId?: string | null
): string | null {
    const { idToken, authReady } = useAuth('authenticated-blob-image');
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const resolvedBlobId = blobId?.trim() || parseBlobIdFromImageUrl(imageUrl);

    useEffect(() => {
        if (!resolvedBlobId || !authReady || !idToken) {
            setObjectUrl(null);
            return;
        }

        let cancelled = false;
        let createdUrl: string | null = null;

        void (async () => {
            try {
                const endpoint = `${getPrimaryFunctionAppRoot()}/GetBlob?id=${encodeURIComponent(resolvedBlobId)}&it=${idToken}`;
                const response = await fetch(endpoint);
                if (!response.ok) return;
                const blob = await response.blob();
                if (!blob.type.startsWith('image/')) return;
                createdUrl = URL.createObjectURL(blob);
                if (!cancelled) setObjectUrl(createdUrl);
                else URL.revokeObjectURL(createdUrl);
            } catch {
                if (!cancelled) setObjectUrl(null);
            }
        })();

        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [resolvedBlobId, authReady, idToken]);

    if (isDirectHttpImageUrl(imageUrl) && !resolvedBlobId) return imageUrl;
    return objectUrl;
}

type AuthenticatedImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
    blobId?: string | null;
};

export function AuthenticatedImg({ src, blobId, alt, ...imgProps }: AuthenticatedImgProps) {
    const resolvedSrc = useAuthenticatedImageSrc(typeof src === 'string' ? src : undefined, blobId);
    if (!resolvedSrc) return null;
    return <img src={resolvedSrc} alt={alt ?? ''} {...imgProps} />;
}
